import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CartAndLineAttributeMap } from "@/cart/types";
import { GUIDType } from "./guidTypes";
import type { Attribute } from "@/graphql/storefront/generated";
import useCart from "@/cart/useCart";
import { isMatic } from "@/utils/ShopifyHelpers";
import { getProductById } from "@/utils/queries";
import {
  MATIC_CARE_PRODUCT_ID,
  MATIC_CARE_VARIANT_ID,
  PET_BRUSH_ROLL_PRODUCT_ID,
  PET_BRUSH_ROLL_VARIANT_ID,
} from "@/consts/shopify";

const GIFT_KEYS = {
  RECIPIENT_FIRST_NAME: "Recipient First Name",
  RECIPIENT_LAST_NAME: "Recipient Last Name",
  RECIPIENT_EMAIL: "Recipient Email",
  LINE1: "Message Top Text",
  LINE2: "Message Bottom Text",
  GUID_TYPE: "Background Image",
} as const;
const GIFT_ATTRIBUTE_KEYS = new Set<string>(Object.values(GIFT_KEYS));

type ProductVariantDetails = {
  unitPrice: number;
  currencyCode: string;
  title: string;
};

const productVariantCache = new Map<string, ProductVariantDetails>();
const productVariantRequests = new Map<
  string,
  Promise<ProductVariantDetails>
>();

const stripGiftAttributes = (attributes: Array<Attribute> | undefined) => {
  return Object.fromEntries(
    (attributes ?? [])
      .filter(
        (attribute): attribute is Attribute =>
          Boolean(attribute?.key) && !GIFT_ATTRIBUTE_KEYS.has(attribute.key),
      )
      .map((attribute) => [attribute.key, attribute.value ?? ""]),
  );
};

type UseGiftAttributeInitializerArgs = {
  attributes: Array<Attribute> | undefined;
  setIsChecked: (value: boolean) => void;
  setFirstName: Dispatch<SetStateAction<string | undefined>>;
  setLastName: Dispatch<SetStateAction<string | undefined>>;
  setEmail: Dispatch<SetStateAction<string | undefined>>;
  setLine1: Dispatch<SetStateAction<string | undefined>>;
  setLine2: Dispatch<SetStateAction<string | undefined>>;
  setSelectedGuidType: Dispatch<SetStateAction<GUIDType | null>>;
};

export const useGiftAttributeInitializer = ({
  attributes,
  setIsChecked,
  setFirstName,
  setLastName,
  setEmail,
  setLine1,
  setLine2,
  setSelectedGuidType,
}: UseGiftAttributeInitializerArgs) => {
  const hasInitializedFromAttributes = useRef(false);

  const getAttributeValue = useCallback(
    (key: string) => {
      return (
        attributes?.find((attribute) => attribute?.key === key)?.value ??
        undefined
      );
    },
    [attributes],
  );

  useEffect(() => {
    if (hasInitializedFromAttributes.current || attributes == undefined) {
      return;
    }

    const anyAttributesSet = Object.values(GIFT_KEYS).some((key) => {
      const val = getAttributeValue(key);
      return val !== undefined && val !== "";
    });

    if (!anyAttributesSet) {
      return;
    }

    setIsChecked(true);
    setFirstName(getAttributeValue(GIFT_KEYS.RECIPIENT_FIRST_NAME));
    setLastName(getAttributeValue(GIFT_KEYS.RECIPIENT_LAST_NAME));
    setEmail(getAttributeValue(GIFT_KEYS.RECIPIENT_EMAIL));
    setLine1(getAttributeValue(GIFT_KEYS.LINE1));
    setLine2(getAttributeValue(GIFT_KEYS.LINE2));

    const attributeGuidType = getAttributeValue(GIFT_KEYS.GUID_TYPE);
    const guidType =
      Object.values(GUIDType).find((type) => type === attributeGuidType) ??
      GUIDType.Party;
    setSelectedGuidType(guidType);

    hasInitializedFromAttributes.current = true;
  }, [
    attributes,
    getAttributeValue,
    setEmail,
    setFirstName,
    setIsChecked,
    setLastName,
    setLine1,
    setLine2,
    setSelectedGuidType,
  ]);

  const resetInitialized = useCallback(() => {
    hasInitializedFromAttributes.current = false;
  }, []);

  return { resetInitialized };
};

export type SetGiftAttributesArgs = {
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  line1: string | undefined;
  line2: string | undefined;
  selectedGuidType: GUIDType | null;
  isChecked: boolean;
};

export const useSetGiftAttributes = ({
  firstName,
  lastName,
  email,
  line1,
  line2,
  selectedGuidType,
  isChecked,
}: SetGiftAttributesArgs) => {
  const { cart, setCartAndLineAttributes } = useCart();

  const maticItem = useMemo(() => {
    return cart?.lines.edges.find(({ node }) => isMatic(node));
  }, [cart]);

  const buildAttributes = useCallback(() => {
    if (!isChecked) {
      return {};
    }
    const attributes: CartAndLineAttributeMap = {
      [GIFT_KEYS.RECIPIENT_FIRST_NAME]: firstName,
      [GIFT_KEYS.RECIPIENT_LAST_NAME]: lastName,
      [GIFT_KEYS.RECIPIENT_EMAIL]: email,
      [GIFT_KEYS.LINE1]: line1,
      [GIFT_KEYS.LINE2]: line2,
      [GIFT_KEYS.GUID_TYPE]: selectedGuidType,
    };
    return attributes;
  }, [email, firstName, isChecked, lastName, line1, line2, selectedGuidType]);

  const updateCartAttributes = useCallback(async () => {
    if (maticItem === undefined) {
      throw new Error("Gift attributes require a Matic line in the cart");
    }
    const attributes = buildAttributes();
    await setCartAndLineAttributes(maticItem.node.id, {
      cartAttributes: attributes,
      lineAttributes: attributes,
    });
  }, [buildAttributes, maticItem, setCartAndLineAttributes]);

  return updateCartAttributes;
};

type UseGiftFormArgs = {
  isGift: boolean;
  setIsGift: (value: boolean) => void;
};

export const useGiftForm = ({ isGift, setIsGift }: UseGiftFormArgs) => {
  const { cart, setCartAndLineAttributes } = useCart();
  const attributes = cart?.attributes;

  const [selectedGuidType, setSelectedGuidType] = useState<GUIDType | null>(
    null,
  );
  const [line1, setLine1] = useState<string | undefined>(undefined);
  const [line2, setLine2] = useState<string | undefined>(undefined);
  const [firstName, setFirstName] = useState<string | undefined>(undefined);
  const [lastName, setLastName] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);

  const { resetInitialized } = useGiftAttributeInitializer({
    attributes,
    setIsChecked: setIsGift,
    setFirstName,
    setLastName,
    setEmail,
    setLine1,
    setLine2,
    setSelectedGuidType,
  });

  const commitAttributes = useSetGiftAttributes({
    firstName,
    lastName,
    email,
    line1,
    line2,
    selectedGuidType,
    isChecked: isGift,
  });

  const maticItem = useMemo(() => {
    return cart?.lines.edges.find(({ node }) => isMatic(node));
  }, [cart]);

  const resetForm = useCallback(() => {
    setSelectedGuidType(null);
    setLine1(undefined);
    setLine2(undefined);
    setFirstName(undefined);
    setLastName(undefined);
    setEmail(undefined);
    setIsGift(false);
    resetInitialized();

    setCartAndLineAttributes(maticItem?.node.id, {
      cartAttributes: stripGiftAttributes(attributes),
      lineAttributes: stripGiftAttributes(maticItem?.node.attributes),
    });
  }, [
    attributes,
    maticItem,
    setCartAndLineAttributes,
    setIsGift,
    resetInitialized,
  ]);

  const isValid =
    selectedGuidType !== null &&
    Boolean(
      line1?.trim() &&
      line2?.trim() &&
      firstName?.trim() &&
      lastName?.trim() &&
      email?.trim(),
    );

  return {
    selectedGuidType,
    setSelectedGuidType,
    line1,
    setLine1,
    line2,
    setLine2,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    isValid,
    commitAttributes,
    resetForm,
  };
};

export type GiftForm = ReturnType<typeof useGiftForm>;

const loadProductVariant = (
  productId: string,
  variantId: string,
  productName: string,
) => {
  const cacheKey = `${productId}:${variantId}`;
  const cachedDetails = productVariantCache.get(cacheKey);

  if (cachedDetails) {
    return Promise.resolve(cachedDetails);
  }

  const existingRequest = productVariantRequests.get(cacheKey);

  if (existingRequest) {
    return existingRequest;
  }

  const request = getProductById(productId)
    .then((product) => {
      const variant =
        product.variants.edges.find((edge) => edge.node?.id === variantId)
          ?.node ?? null;

      if (!variant) {
        throw new Error(`${productName} variant was not found`);
      }

      const unitPrice = Number(variant.price.amount);

      if (!Number.isFinite(unitPrice)) {
        throw new Error(`${productName} variant price was not numeric`);
      }

      const details = {
        unitPrice,
        currencyCode: variant.price.currencyCode,
        title: product.title,
      };

      productVariantCache.set(cacheKey, details);

      return details;
    })
    .catch((error) => {
      productVariantRequests.delete(cacheKey);
      throw error;
    });

  productVariantRequests.set(cacheKey, request);

  return request;
};

const useProductVariant = (
  productId: string,
  variantId: string,
  productName: string,
) => {
  const cacheKey = `${productId}:${variantId}`;
  const [details, setDetails] = useState<
    ProductVariantDetails | null | undefined
  >(() => productVariantCache.get(cacheKey));

  useEffect(() => {
    const cachedDetails = productVariantCache.get(cacheKey);

    if (cachedDetails) {
      setDetails(cachedDetails);
      return;
    }

    let isCurrent = true;
    setDetails(undefined);

    loadProductVariant(productId, variantId, productName)
      .then((nextDetails) => {
        if (isCurrent) {
          setDetails(nextDetails);
        }
      })
      .catch((error) => {
        if (isCurrent) {
          setDetails(null);
        }
        console.error(`Failed to load ${productName}`, error);
      });

    return () => {
      isCurrent = false;
    };
  }, [cacheKey, productId, productName, variantId]);

  return {
    unitPrice: details?.unitPrice,
    currencyCode: details?.currencyCode,
    title: details?.title,
    isLoading: details === undefined,
  };
};

export const useMaticCarePrice = () =>
  useProductVariant(MATIC_CARE_PRODUCT_ID, MATIC_CARE_VARIANT_ID, "Matic Care");

export const usePetBrushRoll = () =>
  useProductVariant(
    PET_BRUSH_ROLL_PRODUCT_ID,
    PET_BRUSH_ROLL_VARIANT_ID,
    "Pet Brush Roll",
  );
